import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dashboardAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  FileText,
  Calculator,
  FileOutput,
  TrendingUp,
  Clock,
  AlertCircle,
  Package,
  IndianRupee,
  ArrowRight,
} from 'lucide-react';
import AdminDashboard from '../components/dashboard/AdminDashboard';

export default function DashboardPage() {
  const { user, isAdmin, isManager } = useAuth();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [timeRange, setTimeRange] = useState('30d');
  const [loading, setLoading] = useState(true);

  const fetchDashboard = useCallback(async () => {
    try {
      let response;
      if (isAdmin()) {
        response = await dashboardAPI.admin({ time_range: timeRange });
      } else if (isManager()) {
        response = await dashboardAPI.manager();
      } else {
        response = await dashboardAPI.user();
      }
      setMetrics(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [isAdmin, isManager, timeRange]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  // User Dashboard
  if (!isManager()) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-3xl font-heading font-bold uppercase tracking-tight text-foreground">
            Welcome, {user?.name}
          </h1>
          <p className="text-muted-foreground">Your personal dashboard overview</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title="Open Inquiries"
            value={metrics?.open_inquiries || 0}
            icon={FileText}
            color="blue"
            onClick={() => navigate('/inquiries?status=open')}
          />
          <MetricCard
            title="Quotations Generated"
            value={metrics?.quotations_generated || 0}
            icon={FileOutput}
            color="green"
            onClick={() => navigate('/quotations')}
          />
          <MetricCard
            title="Total Quoted Value"
            value={formatCurrency(metrics?.total_quoted_value)}
            icon={IndianRupee}
            color="purple"
            isNumber={false}
          />
          <MetricCard
            title="Conversion Rate"
            value={`${metrics?.conversion_rate || 0}%`}
            icon={TrendingUp}
            color="amber"
            isNumber={false}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="text-lg font-heading uppercase">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4">
              <QuickAction href="/inquiries/new" icon={FileText} label="New Inquiry" />
              <QuickAction href="/parts" icon={Package} label="Search Parts" />
              <QuickAction href="/costings" icon={Calculator} label="View Costings" />
              <QuickAction href="/quotations" icon={FileOutput} label="View Quotations" />
            </CardContent>
          </Card>

          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="text-lg font-heading uppercase">Recent Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground text-sm">
                Your recent inquiries and quotations will appear here.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Manager Dashboard
  if (isManager() && !isAdmin()) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-3xl font-heading font-bold uppercase tracking-tight text-foreground">
            Manager Dashboard
          </h1>
          <p className="text-muted-foreground">Team performance and approval queue</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title="Pending Costing Approvals"
            value={metrics?.pending_costing_approvals || 0}
            icon={Clock}
            color="amber"
            onClick={() => navigate('/costings?status=pending_approval')}
          />
          <MetricCard
            title="Pending Quotation Approvals"
            value={metrics?.pending_quotation_approvals || 0}
            icon={AlertCircle}
            color="red"
            onClick={() => navigate('/quotations?status=pending_approval')}
          />
          <MetricCard
            title="Total Quotations"
            value={metrics?.total_quotations || 0}
            icon={FileOutput}
            color="blue"
            onClick={() => navigate('/quotations')}
          />
          <MetricCard
            title="Total Quoted Value"
            value={formatCurrency(metrics?.total_quoted_value)}
            icon={IndianRupee}
            color="green"
            isNumber={false}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="text-lg font-heading uppercase">Frequently Quoted Parts</CardTitle>
            </CardHeader>
            <CardContent>
              {metrics?.frequently_quoted_parts?.length > 0 ? (
                <div className="space-y-3">
                  {metrics.frequently_quoted_parts.map((part, index) => (
                    <div key={part._id || `part-${index}`} className="flex justify-between items-center">
                      <span className="text-sm truncate">{part._id}</span>
                      <Badge variant="secondary">{part.count} quotes</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">No data available</p>
              )}
            </CardContent>
          </Card>

          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="text-lg font-heading uppercase">Approval Actions</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4">
              <QuickAction href="/costings?status=pending_approval" icon={Calculator} label="Review Costings" />
              <QuickAction href="/quotations?status=pending_approval" icon={FileOutput} label="Review Quotations" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Admin dashboard — hero band → inquiry funnel → action queue (components/dashboard)
  return (
    <AdminDashboard
      metrics={metrics}
      timeRange={timeRange}
      setTimeRange={setTimeRange}
      onRefresh={fetchDashboard}
    />
  );
}

function MetricCard({ title, value, icon: Icon, color, isNumber = true, onClick }) {
  const colorClasses = {
    blue: 'text-blue-600 bg-blue-100',
    green: 'text-emerald-600 bg-emerald-100',
    amber: 'text-amber-600 bg-amber-100',
    red: 'text-red-600 bg-red-100',
    purple: 'text-purple-600 bg-purple-100',
  };

  return (
    <Card 
      className={`card-metric ${onClick ? 'cursor-pointer' : ''}`} 
      onClick={onClick}
      data-testid={`metric-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground font-medium">{title}</p>
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="flex items-center justify-between mt-2">
        <p className={`text-2xl font-bold ${isNumber ? 'font-mono' : ''} text-foreground`}>{value}</p>
        {onClick && (
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
        )}
      </div>
    </Card>
  );
}

function QuickAction({ href, icon: Icon, label }) {
  const navigate = useNavigate();
  
  return (
    <button
      onClick={() => navigate(href)}
      className="flex flex-col items-center justify-center p-4 rounded-lg bg-primary/5 hover:bg-primary/10 border border-transparent hover:border-primary/20 transition-all duration-200"
      data-testid={`quick-action-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <Icon className="h-6 w-6 text-primary mb-2" />
      <span className="text-sm text-foreground text-center font-medium">{label}</span>
    </button>
  );
}
